import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, useReducedMotion } from 'framer-motion';
import {
  AlertCircle,
  BookOpen,
  Briefcase,
  Calendar,
  Check,
  ChevronRight,
  Globe,
  Sparkles,
  Store,
} from 'lucide-react';
import { FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DeploymentStep } from './DeploymentStep';
import { DiscordAutomaticIdle } from './DiscordAutomaticIdle';
import { DomainUnreachableNotice } from './DomainUnreachableNotice';
import { ExternalAppsStep } from './ExternalAppsStep';
import { KnownBadUrlNotice } from './KnownBadUrlNotice';
import { OnboardingProgressDots } from './OnboardingProgressDots';
import { PlaceholderDomainNotice } from './PlaceholderDomainNotice';
import { PricingStepContainer } from './PricingStepContainer';
import { QuickchatDomainNotice } from './QuickchatDomainNotice';
import { isPlaceholderDomain, isQuickchatDomain } from './quickchatDomainCheck';
import {
  AutoOnboardingErrorCategory,
  AutoOnboardingStep,
  extractUrlHost,
  LowQualityReason,
  useAutomaticOnboarding,
} from './useAutomaticOnboarding';
import { useEnterToAdvance } from './useEnterToAdvance';
import { GettingStartedProgressDto, OnboardingBrand } from './useGettingStartedOnboarding';
import type { DiscordGuildSummary } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { UrlInput } from '@/Domain/components/KnowledgeBase/UploadUrlModal/components/UrlInput';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import {
  GettingStartedAutomaticUrlValidatedOutcome,
  PostHogEventEnum,
} from '@/Domain/enum/PostHogEvent.enum';
import { WebsiteModalTypeEnum } from '@/Domain/enum/WebsiteModalType.enum';
import { UploadUrlModalHelper } from '@/Domain/helper/UploadUrlModal.helper';
import { checkDomainResolves } from '@/Domain/helper/dnsResolveCheck';
import {
  classifyKnownBadUrl,
  KnownBadUrlMatch,
  parseHost,
  stripHostArtifacts,
} from '@/Domain/helper/scrapeUrlClassifier';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useUser } from '@/Domain/hooks/User/useUser.hook';
import { CAL_LINK, useCalModal } from '@/Domain/hooks/useCalModal.hook';
import { cn } from '@/Domain/utils/cn';
import { forgetSiteMap } from '@/UI/KnowledgeBase/SiteScrapeReview/siteMapFlags';

/**
 * Automatic onboarding flow: user provides a URL and the backend customizes
 * their AI Agent in real-time, pushing incremental progress via websockets.
 *
 * State machine:
 *   idle       → URL input form
 *   processing → visual progress card (updates in-place, no jumping)
 *   completed  → embedded widget preview
 *   error      → error message with retry
 */

type SummaryItem = { icon: string; label: string };

/** Cycles through action verbs with a fade transition. */
const RotatingVerb = () => {
  const { t } = useTranslation('getting_started');
  // The 20-item verb list lives in `getting_started.automatic_flow.loading_verbs`
  // per locale; `returnObjects` returns the JSON array as-is. `useMemo` keyed on
  // `t` keeps the array identity stable across renders so the interval effect
  // below doesn't thrash. Fallback to an empty array if the translation ever
  // drifts into a non-array (defensive; `t` types it as `unknown`).
  const verbs = useMemo(() => {
    const raw = t('automatic_flow.loading_verbs', { returnObjects: true });
    return Array.isArray(raw) ? (raw as string[]) : [];
  }, [t]);
  const [index, setIndex] = useState(() =>
    verbs.length > 0 ? Math.floor(Math.random() * verbs.length) : 0,
  );
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (verbs.length === 0) return;
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % verbs.length);
        setVisible(true);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, [verbs.length]);

  if (verbs.length === 0) return null;

  return (
    <p
      className={cn(
        'text-sm loading-text [animation-duration:6s] transition-opacity duration-300',
        visible ? 'opacity-100' : 'opacity-0',
      )}
    >
      {verbs[index]}…
    </p>
  );
};

const HERO_INITIAL = { opacity: 0, filter: 'blur(8px)', y: 8 } as const;
const HERO_ANIMATE = { opacity: 1, filter: 'blur(0px)', y: 0 } as const;
const HERO_TRANSITION = { duration: 0.6, ease: [0.22, 1, 0.36, 1] } as const;

// Every `welcome_title` translation contains this literal verbatim so it's safe to slice on.
const BRAND_NAME = 'Quickchat AI';
export const BRAND_GRADIENT = 'linear-gradient(90deg, #d4b830 10%, #4d3cfe 60%)';
// Blurple gradient for the Discord-branded idle screen's highlighted word.
export const DISCORD_GRADIENT = 'linear-gradient(90deg, #5865f2 0%, #7d8bff 100%)';

export function renderWelcomeTitle(
  title: string,
  highlight: string,
  gradient: string = BRAND_GRADIENT,
): ReactNode {
  const idx = highlight ? title.indexOf(highlight) : -1;
  if (idx < 0) return title;
  return (
    <>
      {title.slice(0, idx)}
      <span className="bg-clip-text text-transparent" style={{ backgroundImage: gradient }}>
        {highlight}
      </span>
      {title.slice(idx + highlight.length)}
    </>
  );
}

// A whitespace-only first_name is truthy but renders "Welcome,  !" and breaks the highlight slice; treat blank as absent.
export function greetingFirstName(firstName: string | null | undefined): string | null {
  const trimmed = firstName?.trim();
  return trimmed ? trimmed : null;
}

// Language names are resolved on the backend using quickchat.translation.languages.language_index

/** Extract accumulated visual state from the step stream. */
function useProgressSummary(steps: AutoOnboardingStep[]) {
  return useMemo(() => {
    let phase: 'analyzing' | 'scraping' | 'configuring' | 'done' = 'analyzing';
    let pageTitle: string | null = null;
    let avatarUrl: string | null = null;
    let agentName: string | null = null;
    let welcomeMessage: string | null = null;
    let isFinished = false;
    let summaryItems: SummaryItem[] = [];

    for (const step of steps) {
      if (step.step === 'page_scraped') {
        phase = 'scraping';
        pageTitle = step.data?.title || null;
      }
      if (step.step === 'avatar_resolved') {
        avatarUrl = step.data?.avatar_url || null;
      }
      if (step.step === 'settings_generating') {
        phase = 'configuring';
      }
      if (step.step === 'setting_applied') {
        if (step.data?.setting === 'ai_agent_name') agentName = step.data.value ?? null;
        if (step.data?.setting === 'welcome_message') welcomeMessage = step.data.value ?? null;
        if (step.data?.setting === 'avatar') avatarUrl = step.data.value ?? avatarUrl;
      }
      if (step.step === 'completed') {
        phase = 'done';
        isFinished = true;
        summaryItems = (step.data?.summary as unknown as SummaryItem[]) || [];
      }
    }

    return {
      phase,
      pageTitle,
      avatarUrl,
      agentName,
      welcomeMessage,
      isFinished,
      summaryItems,
    };
  }, [steps]);
}

// Phase → translation-key suffix.
const PHASE_LABEL_KEY: Record<string, string> = {
  analyzing: 'automatic_flow.phase.analyzing',
  scraping: 'automatic_flow.phase.scraping',
  configuring: 'automatic_flow.phase.configuring',
  done: 'automatic_flow.phase.done',
};

const UrlInputForm = ({
  onSubmit,
  onSkip,
  isSubmitting,
  urlDraft,
  onUrlDraftChange,
  brand = 'default',
}: {
  onSubmit: (url: string) => void;
  /**
   * When provided, renders a "Skip" link-style button to the left of Start.
   * Clicking it bails out of the URL-first path and hands the user to the
   * manual checklist — same visual weight as the `Back` links elsewhere in
   * the flow so the two bail-out actions share a vocabulary.
   */
  onSkip?: () => void;
  isSubmitting: boolean;
  /**
   * The URL the user has typed so far. Lifted to the page level so that
   * Skip → manual → Back → URL keeps what was typed instead of resetting
   * the field on every mode toggle (motion's `key={mode}` remount).
   */
  urlDraft: string;
  onUrlDraftChange: (next: string) => void;
  /** 'discord' swaps the greeting copy + gradient and pins text to the deep-space palette. */
  brand?: OnboardingBrand;
}) => {
  const { t } = useTranslation('getting_started');
  const { capture } = usePostHogAnalytics();
  // Greet the user by name from Google signup or the register form, falling back
  // to a generic Quickchat AI welcome when the name is missing or blank.
  const { user } = useUser();
  const reduceMotion = useReducedMotion();
  const isDiscord = brand === 'discord';
  const greetingName = greetingFirstName(user?.first_name);
  const welcomeTitle = greetingName
    ? t(
        isDiscord
          ? 'automatic_flow.discord.welcome_title_with_name'
          : 'automatic_flow.welcome_title_with_name',
        { name: greetingName },
      )
    : t(isDiscord ? 'automatic_flow.discord.welcome_title' : 'automatic_flow.welcome_title');
  // Discord title has no "Quickchat AI" to gradient, so highlight "Discord" instead.
  const welcomeHighlight = greetingName || (isDiscord ? 'Discord' : BRAND_NAME);
  // `UrlInput` emits the full `https://`-prefixed URL via `onChange` (see
  // `AppHelper.addProtocol`) — same value shape as the KB modal and the
  // manual-onboarding KB step, so we pass it straight to `submitUrl`.
  const url = urlDraft;
  const setUrl = onUrlDraftChange;
  // Failing validation outcome (null = valid): drives the red border + inline message, cleared the moment the user types.
  const [urlErrorOutcome, setUrlErrorOutcome] =
    useState<GettingStartedAutomaticUrlValidatedOutcome | null>(null);
  // Cleaned URL pending the quickchat-domain notice; null when no warning is showing.
  const [quickchatNoticeUrl, setQuickchatNoticeUrl] = useState<string | null>(null);
  // Placeholder-domain notice (e.g. user submitted `yourcompany.com` verbatim).
  // Boolean rather than URL because there's nothing the user can do with the
  // typed value except go back and replace it.
  const [placeholderNoticeShown, setPlaceholderNoticeShown] = useState(false);
  // Known-bad URL notice (Discord invites, Telegram channels, social-media
  // profile pages — anything `scrapeUrlClassifier` knows yields empty Firecrawl
  // scrapes). We hold the match so the notice copy can show the host + category.
  const [knownBadUrlMatch, setKnownBadUrlMatch] = useState<KnownBadUrlMatch | null>(null);
  // True while the client-side DoH existence check is in flight (drives the
  // Start button's spinner so the brief ~50-150ms lookup doesn't feel like a hang).
  const [checkingDomain, setCheckingDomain] = useState(false);
  // Cleaned URL pending the "domain doesn't exist" notice; null when not showing.
  const [unreachableUrl, setUnreachableUrl] = useState<string | null>(null);
  // Auto-focus the URL field as soon as the form mounts so the user can
  // start typing immediately — the URL step is the primary action on the
  // whole getting-started page, and jumping the caret into it removes one
  // extra click on a brand-new session.
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleUrlChange = (newUrl: string) => {
    if (urlErrorOutcome) setUrlErrorOutcome(null);
    setUrl(newUrl);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // Normalize common paste artifacts before validating: trim edge
    // whitespace, plus any whitespace immediately after the auto-prepended
    // `https://`. The latter shows up when a user pastes something like
    // `   quickchat.ai` — `AppHelper.addProtocol` doesn't recognize a
    // whitespace-prefixed string as already-schemed and stamps `https://`
    // on the front, producing `https://   quickchat.ai`. With this regex
    // the URL collapses back to `https://quickchat.ai` before submit.
    // Internal whitespace anywhere else in the URL (e.g.
    // `example.com fnerlkfre`) still falls through to the inline `\s` check
    // below and surfaces as the inline red border.
    const cleaned = stripHostArtifacts(url.trim().replace(/^(https?:\/\/)\s+/, '$1'));
    // Order matters: empty → whitespace → invalid_format → valid. The outcome
    // doubles as the PostHog property and the early-return selector below.
    let outcome: GettingStartedAutomaticUrlValidatedOutcome;
    if (!cleaned) outcome = 'empty';
    else if (/\s/.test(cleaned)) outcome = 'has_whitespace';
    else if (!UploadUrlModalHelper.validateUrl(cleaned)) outcome = 'invalid_format';
    else outcome = 'valid';

    // Fires on every submit click — including rejections — so the funnel can
    // split "submitted garbage" from "submitted real-looking URL that the
    // backend later DNS-failed". `url_host` is undefined on rejection
    // branches since `extractUrlHost` only parses valid-shape URLs.
    capture(PostHogEventEnum.GettingStartedAutomaticUrlValidated, {
      outcome,
      url_host: extractUrlHost(cleaned),
    });

    if (outcome !== 'valid') {
      setUrlErrorOutcome(outcome);
      return;
    }
    setUrlErrorOutcome(null);
    // Reflect the cleanup back into the parent's URL draft so a post-error
    // retry doesn't show the original whitespace-laden value in the field.
    if (cleaned !== url) setUrl(cleaned);
    if (isQuickchatDomain(cleaned)) {
      capture(PostHogEventEnum.GettingStartedQuickchatDomainWarningShown, {
        url_host: parseHost(cleaned),
      });
      setQuickchatNoticeUrl(cleaned);
      return;
    }
    if (isPlaceholderDomain(cleaned)) {
      capture(PostHogEventEnum.GettingStartedPlaceholderDomainWarningShown, {
        url_host: parseHost(cleaned),
      });
      setPlaceholderNoticeShown(true);
      return;
    }
    const knownBad = classifyKnownBadUrl(cleaned);
    if (knownBad) {
      capture(PostHogEventEnum.GettingStartedKnownBadUrlWarningShown, {
        category: knownBad.category,
        url_host: knownBad.urlHost,
      });
      setKnownBadUrlMatch(knownBad);
      return;
    }
    // Conservative client-side DNS check (see dnsResolveCheck): only a
    // definitive NXDOMAIN stops the user here, turning the typo'd-domain
    // failure — which today only surfaces 60-150s later when the background
    // scrape fails — into instant inline feedback. A resolver outage or
    // ambiguous answer returns 'unknown' and falls straight through to submit.
    setCheckingDomain(true);
    const resolution = await checkDomainResolves(cleaned);
    setCheckingDomain(false);
    if (resolution === 'nxdomain') {
      capture(PostHogEventEnum.GettingStartedDomainUnreachableWarningShown, {
        url_host: parseHost(cleaned),
      });
      setUnreachableUrl(cleaned);
      return;
    }
    onSubmit(cleaned);
  };

  const dismissPlaceholderNotice = () => {
    setPlaceholderNoticeShown(false);
    // Clear the field so the user types their real URL on a clean slate
    // instead of editing the placeholder string in place.
    setUrl('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  // quickchat.ai is a hard block: the backend own-domain gate rejects it, so we
  // only offer "correct the URL" — no proceed path that would 400.
  const dismissQuickchatNotice = () => {
    const noticeUrl = quickchatNoticeUrl;
    capture(PostHogEventEnum.GettingStartedQuickchatDomainWarningResolved, {
      outcome: 'corrected',
      url_host: noticeUrl ? parseHost(noticeUrl) : undefined,
    });
    setQuickchatNoticeUrl(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const dismissKnownBadUrlNotice = (outcome: 'corrected' | 'switched_to_manual') => {
    const matched = knownBadUrlMatch;
    capture(PostHogEventEnum.GettingStartedKnownBadUrlWarningResolved, {
      outcome,
      category: matched?.category,
      url_host: matched?.urlHost,
    });
    setKnownBadUrlMatch(null);
    if (outcome === 'switched_to_manual' && onSkip) {
      onSkip();
      return;
    }
    // 'corrected' — clear the field so the user types a new URL on a clean
    // slate and refocus to keep typing flow uninterrupted.
    setUrl('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const dismissUnreachableNotice = (outcome: 'corrected' | 'proceeded' | 'switched_to_manual') => {
    const noticeUrl = unreachableUrl;
    capture(PostHogEventEnum.GettingStartedDomainUnreachableWarningResolved, {
      outcome,
      url_host: noticeUrl ? parseHost(noticeUrl) : undefined,
    });
    setUnreachableUrl(null);
    if (outcome === 'proceeded' && noticeUrl) {
      onSubmit(noticeUrl);
      return;
    }
    if (outcome === 'switched_to_manual' && onSkip) {
      onSkip();
      return;
    }
    // 'corrected' (or manual with no Skip handler) — clear the field so the
    // user retypes on a clean slate, and refocus to keep typing flow going.
    setUrl('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  if (quickchatNoticeUrl) {
    return (
      <QuickchatDomainNotice
        submittedUrl={quickchatNoticeUrl}
        onCorrect={() => dismissQuickchatNotice()}
      />
    );
  }

  if (placeholderNoticeShown) {
    return <PlaceholderDomainNotice onBack={dismissPlaceholderNotice} />;
  }

  if (knownBadUrlMatch) {
    return (
      <KnownBadUrlNotice
        category={knownBadUrlMatch.category}
        urlHost={knownBadUrlMatch.urlHost}
        onCorrect={() => dismissKnownBadUrlNotice('corrected')}
        onSwitchToManual={() => dismissKnownBadUrlNotice('switched_to_manual')}
      />
    );
  }

  if (unreachableUrl) {
    return (
      <DomainUnreachableNotice
        urlHost={parseHost(unreachableUrl) ?? unreachableUrl}
        onCorrect={() => dismissUnreachableNotice('corrected')}
        onProceed={() => dismissUnreachableNotice('proceeded')}
        onSwitchToManual={() => dismissUnreachableNotice('switched_to_manual')}
      />
    );
  }

  // The Start button stays busy through both the in-flight POST and the
  // pre-submit DoH lookup so the field/CTA can't be re-fired mid-check.
  const busy = isSubmitting || checkingDomain;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      {/* Three-line header with a clear visual hierarchy:
            h1  — the personalized greeting, the biggest+boldest element
            h2  — the step goal, medium weight / muted so it reads as a
                  subtitle under the greeting rather than competing with it
            label — standard form label weight, tied to the URL input below
          `mb-4` on top of the form's `gap-6` gives the welcome header a
          noticeably wider separation from the URL input group below it —
          the greeting reads as its own block before the user's attention
          moves down to the field. */}
      <div className="flex flex-col gap-3 mb-4 text-center">
        <motion.h1
          className={cn(
            'font-safiro text-[2.75rem] font-medium text-balance leading-[1.1]',
            isDiscord ? 'text-[var(--qc-discord-on-surface)]' : 'text-foreground',
          )}
          data-testid="gs-automatic-welcome"
          initial={reduceMotion ? undefined : HERO_INITIAL}
          animate={HERO_ANIMATE}
          transition={HERO_TRANSITION}
        >
          {renderWelcomeTitle(
            welcomeTitle,
            welcomeHighlight,
            isDiscord ? DISCORD_GRADIENT : BRAND_GRADIENT,
          )}
        </motion.h1>
        <motion.h2
          className={cn(
            'text-lg font-medium',
            isDiscord ? 'text-[var(--qc-discord-muted)]' : 'text-muted-foreground',
          )}
          initial={reduceMotion ? undefined : HERO_INITIAL}
          animate={HERO_ANIMATE}
          transition={{ ...HERO_TRANSITION, delay: 0.4 }}
        >
          {t(isDiscord ? 'automatic_flow.discord.input_title' : 'automatic_flow.input_title')}
        </motion.h2>
      </div>

      {/* Interactive controls (URL input + Start, skip link) are bounded
          to `max-w-sm` so the form column stays at a comfortable typing
          width even though the outer container went wide to give the
          hero copy room to breathe. */}
      <div className="w-full max-w-sm mx-auto flex flex-col gap-2">
        {/* Shared `UrlInput` — same component used by the KB tab's
            `UploadUrlModal` and by the manual-onboarding `KnowledgeBaseStep`.
            Keeps the `https://` start adornment, focus/error styling, and
            validation error strings in lockstep across the three entry points.
            The subtitle above (`input_title`) already describes the field, so
            no separate inline label is needed. */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0" data-testid="gs-automatic-url-input">
              <UrlInput
                ref={inputRef}
                type={WebsiteModalTypeEnum.SINGLE}
                value={url}
                onChange={handleUrlChange}
                error={urlErrorOutcome !== null}
                disabled={busy}
                className="[&>div:first-child]:!px-3"
              />
            </div>
            <Button
              size="md"
              type="submit"
              disabled={busy}
              className="min-w-16 shrink-0 px-3"
              data-testid="gs-automatic-submit"
            >
              {busy ? <Spinner className="size-4" /> : t('automatic_flow.submit')}
            </Button>
          </div>
          {/* Fixed-height slot reserved for the validation message: always
              present (sized for two lines — the longest translated string) so
              the message fades in without growing the column. Without it the
              `my-auto`-centered layout re-centers on every error, jumping the
              hero up and the actions below down. */}
          <div className="min-h-[2.5rem] px-1" aria-live="polite">
            {urlErrorOutcome && (
              <motion.div
                role="alert"
                initial={reduceMotion ? undefined : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15 }}
                className="flex items-start gap-1.5 text-destructive text-sm"
                data-testid="gs-automatic-url-error"
              >
                <AlertCircle className="size-4 shrink-0 mt-0.5" />
                <span>{t(`automatic_flow.url_error.${urlErrorOutcome}`)}</span>
              </motion.div>
            )}
          </div>
        </div>

        {onSkip && (
          <div className="flex justify-center">
            <Button
              size="sm"
              variant="link"
              type="button"
              onClick={onSkip}
              disabled={busy}
              className={cn(
                'hover:!opacity-100',
                isDiscord
                  ? '!text-[var(--qc-discord-muted)] hover:!text-[var(--qc-discord-on-surface)]'
                  : '!text-foreground/60 hover:!text-foreground',
              )}
              data-testid="gs-automatic-skip"
            >
              {t('automatic_flow.skip_to_manual')}
              <ChevronRight className="size-4" />
            </Button>
          </div>
        )}
      </div>
    </form>
  );
};

/**
 * Secondary CTA rendered under the URL-first card. Opens the same Cal.com
 * modal that the deleted `OnboardingModeSelection` surfaced — the booking
 * link, theme sync, and PostHog event all stay identical so funnel data
 * comparing the two eras remains meaningful.
 */
const BookCallOption = () => {
  const { t } = useTranslation('getting_started');
  const openCalModal = useCalModal();
  const { capture } = usePostHogAnalytics();
  // The Cal.com modal opens via an async `getCalApi()` round-trip (~100-300ms
  // on a cold load). Without a pending guard the user can fire 3+ clicks
  // before the modal paints — each one fires `getting_started_call_booked`
  // and triggers `cal('modal', ...)`, inflating the funnel metric and
  // queueing duplicate modal-open commands. PostHog production data showed
  // 4 of 27 cohort users emitting 3 call-booked events within 1-2 seconds,
  // confirming the rage-click pattern.
  // Single-fire guard so a triple-click only opens the Cal.com modal once and
  // fires the PostHog event once. A ref (not state) keeps it cheap — we don't
  // need a re-render, just a synchronous "are we already opening?" check.
  const isOpeningRef = useRef(false);

  const handleBookCall = async () => {
    if (isOpeningRef.current) return;
    isOpeningRef.current = true;
    capture(PostHogEventEnum.GettingStartedCallBooked);
    try {
      await openCalModal(CAL_LINK.productDemo, 'getting_started');
    } finally {
      isOpeningRef.current = false;
    }
  };

  return (
    <Button
      type="button"
      onClick={handleBookCall}
      data-testid="gs-automatic-book-call"
      className="w-full"
    >
      <Calendar />
      {t('automatic_flow.book_call')}
    </Button>
  );
};

const ProgressView = ({
  steps,
  submittedUrl,
  warningReason,
  footer,
}: {
  steps: AutoOnboardingStep[];
  submittedUrl: string | null;
  warningReason: LowQualityReason | null;
  footer?: ReactNode;
}) => {
  const { t } = useTranslation('getting_started');
  const { phase, pageTitle, avatarUrl, agentName, welcomeMessage, isFinished, summaryItems } =
    useProgressSummary(steps);

  const phaseKey = PHASE_LABEL_KEY[phase];
  const phaseLabel = phaseKey ? t(phaseKey) : t('automatic_flow.working');

  // Extract domain from submitted URL for immediate display
  const domain = useMemo(() => {
    if (!submittedUrl) return null;
    return parseHost(submittedUrl) ?? submittedUrl;
  }, [submittedUrl]);

  // Show the preview card as soon as we have the avatar (prefetched in ~0.3s)
  // or once settings start arriving
  const showPreviewCard = avatarUrl || agentName || domain;

  return (
    <div
      className={cn(
        'bg-card border border-border rounded-2xl p-6 flex flex-col gap-6',
        footer && 'min-h-[500px]',
      )}
    >
      {/* Header — always stable, updates in-place. On completion the phase
          label swaps to the "ready" copy; when the homepage was low-quality
          it instead reframes to a calm "preview / start over" so the header
          doesn't celebrate a result the warning below is cautioning against. */}
      <div>
        <p className="text-xl font-semibold text-foreground">
          {isFinished && warningReason ? t('automatic_flow.low_quality.summary_title') : phaseLabel}
        </p>
        <p className="text-sm font-medium text-muted-foreground mt-1">
          {!isFinished
            ? t('automatic_flow.timing_hint')
            : warningReason
              ? t('automatic_flow.low_quality.summary_hint')
              : t('automatic_flow.ready_hint')}
        </p>
      </div>

      {warningReason && (
        <Alert variant="warning" data-testid="gs-automatic-low-quality">
          <AlertTitle>{t('automatic_flow.low_quality.title')}</AlertTitle>
          <AlertDescription>
            {t(LOW_QUALITY_COPY_KEY[warningReason], { domain: domain ?? submittedUrl ?? '' })}
          </AlertDescription>
        </Alert>
      )}

      {/* Page title — show domain immediately, upgrade to full title when scraped */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground transition-opacity duration-300">
        <Globe className="size-3.5 text-muted-foreground flex-shrink-0" />
        <span className="truncate" data-testid="gs-automatic-progress-domain">
          {pageTitle || domain || submittedUrl}
        </span>
      </div>

      {/* Agent preview card — shows domain+avatar immediately, fills in as settings arrive */}
      {showPreviewCard && (
        <div className="border border-border rounded-xl overflow-hidden transition-all duration-300">
          {/* Mini header bar */}
          <div className="h-10 flex items-center gap-2.5 px-3 bg-muted transition-colors duration-500">
            {avatarUrl && (
              <img
                // Key on the URL so React re-mounts the element instead of
                // reusing the previous DOM node when the backend upgrades
                // the avatar mid-run. Without the key, an `onError`-inlined
                // `display: none` sticks to the element and hides a later,
                // valid URL too.
                key={avatarUrl}
                src={avatarUrl}
                alt=""
                className="size-6 rounded-full object-contain bg-background/90 transition-opacity duration-300"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <span className="text-sm font-medium truncate text-foreground transition-colors duration-300">
              {agentName || domain}
            </span>
          </div>

          {/* Welcome message area */}
          <div className="px-3 py-2.5 min-h-[2.5rem] flex items-center">
            {welcomeMessage ? (
              <p className="text-sm text-foreground transition-opacity duration-300">
                {welcomeMessage}
              </p>
            ) : !isFinished ? (
              <RotatingVerb />
            ) : null}
          </div>
        </div>
      )}

      {/* Summary of what was configured — shown on completion */}
      {isFinished && summaryItems.length > 0 && (
        <div className="flex flex-col gap-2">
          {summaryItems.map((item, i) => {
            const IconComponent =
              item.icon === 'briefcase'
                ? Briefcase
                : item.icon === 'sparkles'
                  ? Sparkles
                  : item.icon === 'globe'
                    ? Globe
                    : item.icon === 'store'
                      ? Store
                      : item.icon === 'book'
                        ? BookOpen
                        : Check;
            const isArticleItem = item.icon === 'book';
            return (
              <div key={i} className="flex items-center gap-2 text-sm text-foreground">
                <IconComponent className="size-4 text-green-500 flex-shrink-0" />
                <span>{isArticleItem ? t('automatic_flow.kb_background_scrape') : item.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {footer && <div className="mt-auto flex items-center justify-end gap-3">{footer}</div>}
    </div>
  );
};

// Post-processing stepper that runs after the backend is done. The automatic
// flow walks the user through the same External Apps, Deployment, and Pricing
// containers as the manual checklist, reusing the components one-for-one.
// Pricing is terminal — clicking Skip-for-now (or picking a tier and returning
// from Stripe) is what finishes the automatic flow.
type PostStep = 'summary' | 'external_apps' | 'deployment' | 'pricing';

const POST_STEP_ORDER: PostStep[] = ['summary', 'external_apps', 'deployment', 'pricing'];

const PostStepProgressDots = ({ currentStep }: { currentStep: PostStep }) => (
  <OnboardingProgressDots
    totalSteps={POST_STEP_ORDER.length}
    currentIndex={POST_STEP_ORDER.indexOf(currentStep)}
    testIdPrefix="gs-automatic-progress-dot"
  />
);

// Translation-key suffix per user-facing category. Keeps the mapping in one
// place so adding a new category means adding one line here + one entry in
// each locale file (keys under `automatic_flow.error.<suffix>`).
const CATEGORY_I18N_KEY: Record<AutoOnboardingErrorCategory, string> = {
  scrape_failed: 'automatic_flow.error.scrape_failed',
  access_blocked: 'automatic_flow.error.access_blocked',
  timeout: 'automatic_flow.error.timeout',
  dns_failure: 'automatic_flow.error.dns_failure',
  website_not_supported: 'automatic_flow.error.website_not_supported',
  bad_request: 'automatic_flow.error.bad_request',
  server_error: 'automatic_flow.error.server_error',
  network: 'automatic_flow.error.network',
  unknown: 'automatic_flow.error.unknown',
};

// Advisory low-quality-source copy. `blocked` = the homepage looked like a
// maintenance/login/error page; `thin` = it read as empty/near-empty;
// `auth_wall` = a sign-in page, where the fix is a different URL rather than a retry.
const LOW_QUALITY_COPY_KEY: Record<LowQualityReason, string> = {
  http_error: 'automatic_flow.low_quality.blocked',
  error_page: 'automatic_flow.low_quality.blocked',
  soft_404: 'automatic_flow.low_quality.thin',
  empty_content: 'automatic_flow.low_quality.thin',
  thin_content: 'automatic_flow.low_quality.thin',
  off_domain_redirect: 'automatic_flow.low_quality.wrong_site',
  auth_wall: 'automatic_flow.low_quality.auth_wall',
};

const ErrorView = ({
  category,
  urlHost,
  onRetry,
  onTryDifferentWebsite,
  onSwitchToManual,
}: {
  category: AutoOnboardingErrorCategory;
  urlHost?: string;
  onRetry: () => void;
  onTryDifferentWebsite?: () => void;
  onSwitchToManual?: () => void;
}) => {
  const { t } = useTranslation('getting_started');
  // Retrying the same bot-walled URL is futile, so lead with manual setup.
  const leadWithManual = category === 'access_blocked' && Boolean(onSwitchToManual);
  return (
    <div className="bg-card border border-destructive/20 rounded-2xl p-6 flex flex-col items-center text-center gap-4">
      <div className="size-12 rounded-xl flex items-center justify-center bg-destructive/10">
        <AlertCircle className="size-6 text-destructive" />
      </div>
      <div className="space-y-1.5">
        <h2 className="text-lg font-semibold text-foreground">{t('automatic_flow.error_title')}</h2>
        <p className="text-sm text-muted-foreground">
          {t(CATEGORY_I18N_KEY[category], { host: urlHost ?? '' })}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 items-center">
        {leadWithManual ? (
          <>
            <Button
              variant="dark"
              size="md"
              onClick={onSwitchToManual}
              data-testid="gs-automatic-switch-to-manual"
            >
              {t('automatic_flow.error_setup_manually')}
            </Button>
            <Button
              variant="link"
              size="md"
              onClick={onTryDifferentWebsite ?? onRetry}
              data-testid="gs-automatic-retry"
            >
              {t('automatic_flow.error_try_different')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="dark" size="md" onClick={onRetry} data-testid="gs-automatic-retry">
              {t('automatic_flow.error_retry')}
            </Button>
            {onSwitchToManual && (
              <Button
                variant="link"
                size="md"
                onClick={onSwitchToManual}
                data-testid="gs-automatic-switch-to-manual"
              >
                {t('automatic_flow.error_switch_to_manual')}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

interface AutomaticOnboardingProps {
  /**
   * Fired once when the backend finishes customizing the agent (state hits
   * `completed`). The parent uses this to flip its own `isOnboardingComplete`
   * flag and swap the page into the widget view — this component no longer
   * mounts the widget itself.
   */
  onComplete?: () => void;
  /**
   * When provided, shows a "Skip" link next to the Start button on the URL
   * form. Clicking it commits the user to the manual checklist flow instead
   * of running the automatic job.
   */
  onSkipToManual?: () => void;
  /**
   * Offered as a secondary action on the error view when a scrape fails —
   * lets the user abandon the URL-driven flow and fall back to the manual
   * checklist without losing the failure context. Distinct from
   * `onSkipToManual` (URL-form Skip) so funnel events can tell the two
   * gestures apart: one is a deliberate up-front choice, the other is a
   * post-failure recovery.
   */
  onSwitchToManual?: () => void;
  /**
   * Page-level URL draft state. Lifted out of this component so the typed
   * URL survives motion's `key={mode}` remount when the user toggles
   * between automatic and manual mode (the previous local-state version
   * cleared the field on every toggle, which forced users to re-type their
   * URL when they came back from the manual checklist).
   *
   * Optional for compatibility with call sites that don't yet thread the
   * state through; when omitted the component falls back to local state.
   */
  urlDraft?: string;
  onUrlDraftChange?: (next: string) => void;
  /** Server-derived onboarding brand; 'discord' renders the Discord-branded idle screen. */
  brand?: OnboardingBrand;
}

interface PostStepCardProps {
  title: string;
  description: string;
  onBack: () => void;
  onNext: () => void;
  nextLabel: string;
  isAdvancing?: boolean;
  children: ReactNode;
}

const PostStepCard = ({
  title,
  description,
  onBack,
  onNext,
  nextLabel,
  isAdvancing = false,
  children,
}: PostStepCardProps) => {
  const { t } = useTranslation('getting_started');
  return (
    <div className="flex flex-col gap-6 bg-card border border-border rounded-xl p-6 min-h-[500px]">
      <div>
        <p className="text-xl font-semibold text-foreground">{title}</p>
        <p className="text-sm font-medium text-muted-foreground mt-1">{description}</p>
      </div>

      <div className="flex-1">{children}</div>

      <div className="flex items-center justify-end gap-3">
        <Button
          size="sm"
          variant="link"
          onClick={onBack}
          disabled={isAdvancing}
          className="!text-foreground/60 hover:!opacity-100 hover:!text-foreground"
          data-testid="gs-automatic-poststep-back"
        >
          {t('back')}
        </Button>
        <Button
          size="sm"
          onClick={onNext}
          loading={isAdvancing}
          className="min-w-16"
          data-testid="gs-automatic-poststep-next"
        >
          {nextLabel}
        </Button>
      </div>
    </div>
  );
};

function getDiscordGuildConnectionTimestamp(guild: DiscordGuildSummary): number {
  const timestamp = Date.parse(guild.connected_at ?? guild.added_at ?? '');
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

export const AutomaticOnboarding = ({
  onComplete,
  onSkipToManual,
  onSwitchToManual,
  urlDraft,
  onUrlDraftChange,
  brand = 'default',
}: AutomaticOnboardingProps) => {
  const { t } = useTranslation('getting_started');
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();
  const {
    state,
    steps,
    errorCategory,
    warningReason,
    submittedUrl,
    submitUrl,
    isSubmitting,
    reset,
  } = useAutomaticOnboarding();
  // Local fallback for the URL draft when the parent hasn't lifted state.
  // Once GettingStartedPage is fully migrated this can become required.
  const [localUrlDraft, setLocalUrlDraft] = useState('');
  const effectiveUrlDraft = urlDraft ?? localUrlDraft;
  const effectiveSetUrlDraft = onUrlDraftChange ?? setLocalUrlDraft;

  // Post-processing stepper state. Only consulted while `state === 'completed'`;
  // reset to `summary` whenever the backend goes back to a non-completed state
  // (e.g. error retry) so re-entering completion lands on the summary again.
  //
  // On fresh mount we derive the resume position from the cached progress row:
  // `external_apps_step_completed=true` means the user already clicked Continue
  // past the external-apps step, so they were on `deployment` when they
  // reloaded. Summary and external_apps collapse to `summary` on refresh —
  // there's no existing DB field that cleanly distinguishes them, and the
  // summary screen is a single Continue click away from external_apps, so the
  // cost of the extra click is minimal.
  // Discord OAuth sets deployment alone, so pricing also requires the wizard-only external-apps flag.
  const [postStep, setPostStep] = useState<PostStep>(() => {
    const progress = queryClient.getQueryData<GettingStartedProgressDto | null>([
      QueryKey.gettingStarted.progress,
    ]);
    if (!progress?.external_apps_step_completed) return 'summary';
    return progress.deployment_step_completed ? 'pricing' : 'deployment';
  });
  // Domain for the resumed low-quality advisory, derived per render: same-mount
  // recovery still has the live submittedUrl (the cache may predate the run),
  // while refresh-resume has only the persisted source URL (refetched by then).
  const cachedSourceUrl = queryClient.getQueryData<GettingStartedProgressDto | null>([
    QueryKey.gettingStarted.progress,
  ])?.automatic_source_url;
  const resumeSourceDomain = submittedUrl
    ? (extractUrlHost(submittedUrl) ?? null)
    : cachedSourceUrl
      ? (extractUrlHost(cachedSourceUrl) ?? null)
      : null;
  useEffect(() => {
    if (state !== 'completed') setPostStep('summary');
  }, [state]);

  const { data: discordGuilds } = useQuery<DiscordGuildSummary[]>({
    queryKey: [QueryKey.integration.discordGuilds],
    enabled: brand === 'discord' && state === 'idle',
  });
  // Prefer OAuth confirmation time, with added_at as a rolling-deploy fallback.
  const connectedGuild = useMemo(
    () =>
      (discordGuilds ?? [])
        .filter((guild) => guild.state === 'ACTIVE' && guild.enabled)
        .reduce<DiscordGuildSummary | null>(
          (newest, guild) =>
            !newest ||
            getDiscordGuildConnectionTimestamp(guild) >= getDiscordGuildConnectionTimestamp(newest)
              ? guild
              : newest,
          null,
        ),
    [discordGuilds],
  );
  const [isPostConnectPricingOpen, setIsPostConnectPricingOpen] = useState(false);
  const showPostConnectPricing = state === 'idle' && isPostConnectPricingOpen;

  // Persist the post-step advance onto the progress row so a refresh can
  // resume mid-wizard. We only have a DB signal for the summary→external_apps
  // and external_apps→deployment transitions (via the existing step-completed
  // flags); no write is needed on pure backwards navigation. Uses the shared
  // mutation defaults from queryClient.ts so we just feed it a delta. On
  // success we merge only the fields we owned back into the cache — see the
  // long comment on `useGettingStartedOnboarding.mutation.onSuccess` for why
  // overwriting with the full server response can regress concurrent writes.
  const progressMutation = useMutation<
    GettingStartedProgressDto,
    Error,
    Partial<GettingStartedProgressDto>
  >({
    mutationKey: [QueryKey.gettingStarted.progress],
    onSuccess: (_updated, variables) => {
      queryClient.setQueryData<GettingStartedProgressDto | null>(
        [QueryKey.gettingStarted.progress],
        (prev) => (prev ? { ...prev, ...variables } : prev),
      );
    },
  });

  const handleDiscordSkipKnowledgeBase = () => {
    const progress = queryClient.getQueryData<GettingStartedProgressDto | null>([
      QueryKey.gettingStarted.progress,
    ]);
    const nextSkipped = Array.from(new Set([...(progress?.skipped_steps ?? []), 'knowledge_base']));
    progressMutation.mutate(
      { skipped_steps: nextSkipped },
      {
        onSuccess: () => {
          capture(PostHogEventEnum.GettingStartedDiscordSkippedKnowledgeBase);
          setIsPostConnectPricingOpen(true);
        },
        onError: () => toast.error(t('automatic_flow.error_title')),
      },
    );
  };

  // "Start over" from a low-quality result must leave nothing behind: the URL
  // screen should be clean, not haunted by the abandoned scrape. So besides
  // resetting the flow we clear the URL draft and tear down the background site
  // map — its dock (flags + cached query) and the backend row it created.
  const discardSiteMapMutation = useMutation({
    mutationKey: [QueryKey.knowledgeBase.siteMapPreview.delete],
  });
  const handleStartOver = () => {
    effectiveSetUrlDraft('');
    forgetSiteMap();
    reset();
    // Persist the reset, not just the local state: completion stamped
    // knowledge_base_step_completed, which drives refresh-resume
    // (isAutomaticResumeable), so a reload would otherwise resurrect this result.
    progressMutation.mutate({ knowledge_base_step_completed: false });
    // Pin the preview to `none` rather than removeQueries — removing makes the
    // still-enabled dock refetch and re-show the row the backend creates right
    // after "completed" — then delete the backend row (its .update() no-ops once gone).
    void queryClient.cancelQueries({ queryKey: [QueryKey.knowledgeBase.siteMapPreview.get] });
    queryClient.setQueryData([QueryKey.knowledgeBase.siteMapPreview.get], {
      status: 'none',
      total_urls: 0,
      host: '',
      tree: {},
    });
    discardSiteMapMutation.mutate();
  };

  // `GettingStartedFinished` (fired from the page) is the terminal conversion
  // event. The per-post-step advances we capture here sit strictly between
  // `GettingStartedAutomaticCompleted` (backend done) and `GettingStartedFinished`
  // so we can measure drop-off at each post-step instead of seeing a single
  // "automatic done" vs. "finished" gap. We only fire on forward advances —
  // going backwards is not a conversion signal.
  const advancePostStep = (next: PostStep) => {
    if (progressMutation.isPending) return;
    capture(PostHogEventEnum.GettingStartedAutomaticPostStepCompleted, {
      completed_step: postStep,
      next_step: next,
    });
    // Mirror the manual-flow semantic: persist each per-step "completed" flag
    // as the user advances past it. `summary→external_apps` is derivable on
    // refresh from the absence of any step flag (default → summary), so it
    // doesn't need its own write.
    if (next === 'deployment') {
      progressMutation.mutate({ external_apps_step_completed: true });
    }
    if (next === 'pricing') {
      progressMutation.mutate({ deployment_step_completed: true });
    }
    setPostStep(next);
  };

  // Terminal action for the automatic flow — flips `automatic_flow_completed` and advances to AI Preview.
  const handlePricingSkipForNow = () => {
    capture(PostHogEventEnum.GettingStartedAutomaticPostStepCompleted, {
      completed_step: 'pricing',
      next_step: 'finished',
    });
    const progress = queryClient.getQueryData<GettingStartedProgressDto | null>([
      QueryKey.gettingStarted.progress,
    ]);
    const nextSkipped = Array.from(new Set([...(progress?.skipped_steps ?? []), 'pricing']));
    progressMutation.mutate({ skipped_steps: nextSkipped });
    onComplete?.();
  };

  const enterHandler = useMemo(() => {
    if (state !== 'completed') return null;
    // A rejected source presents a real choice: Start over is primary and
    // Continue anyway is deliberately secondary. Do not let the page-wide
    // shortcut silently choose the latter; either action must be explicit.
    if (postStep === 'summary') {
      if (warningReason) return null;
      return () => advancePostStep('external_apps');
    }
    if (postStep === 'external_apps') return () => advancePostStep('deployment');
    if (postStep === 'deployment') return () => advancePostStep('pricing');
    if (postStep === 'pricing') return handlePricingSkipForNow;
    return null;
    // Key on `isPending` too: the advance handlers it returns read it via the
    // re-entrancy guard, so without it the closure freezes pending=true from the
    // hop that started the write and Enter stays dead after the write resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, postStep, progressMutation.isPending, warningReason]);
  useEnterToAdvance(enterHandler);

  // Widen the wrapper when the 4-tier grid is showing.
  const isPricingPostStep =
    (state === 'completed' && postStep === 'pricing') || showPostConnectPricing;

  return (
    <div
      className={cn(
        // Horizontal padding keeps the URL input's `focus-within:ring-[3px]`
        // from getting clipped. `overflow-y-auto` implicitly collapses
        // `overflow-x: visible` to `auto` (CSS spec), which means a focus
        // ring extending past the container's left/right edge is cut — the
        // 4px padding on each side gives the 3px ring room to render fully.
        'flex flex-col gap-4 z-10 w-full max-h-[calc(100vh-140px)] px-1 overflow-y-auto custom-scroll',
        isPricingPostStep ? 'max-w-6xl' : 'max-w-2xl',
      )}
      data-testid="gs-onboarding-automatic"
    >
      {showPostConnectPricing && (
        <div className="flex flex-col gap-4">
          <PricingStepContainer
            onBack={() => setIsPostConnectPricingOpen(false)}
            onSkipForNow={handlePricingSkipForNow}
            isSkipPending={progressMutation.isPending}
          />
        </div>
      )}

      {state === 'idle' &&
        !showPostConnectPricing &&
        (brand === 'discord' ? (
          <DiscordAutomaticIdle
            connectedGuildName={connectedGuild?.guild_name ?? null}
            onContinueToPricing={handleDiscordSkipKnowledgeBase}
            isContinuePending={progressMutation.isPending}
            urlForm={
              <UrlInputForm
                onSubmit={submitUrl}
                onSkip={onSkipToManual}
                isSubmitting={isSubmitting}
                urlDraft={effectiveUrlDraft}
                onUrlDraftChange={effectiveSetUrlDraft}
                brand="discord"
              />
            }
          />
        ) : (
          <div className="flex flex-col gap-4">
            <UrlInputForm
              onSubmit={submitUrl}
              onSkip={onSkipToManual}
              isSubmitting={isSubmitting}
              urlDraft={effectiveUrlDraft}
              onUrlDraftChange={effectiveSetUrlDraft}
            />
            {/* Match the form column's `max-w-sm` so the divider + Book-a-call
                CTA align visually with the URL input above them, even though
                the outer container is `max-w-2xl` for the hero header. */}
            <div className="w-full max-w-sm mx-auto flex flex-col gap-4">
              {/* Classic login-page divider: a thin horizontal line on each
                  side with "or" in the middle, signalling that the Book-a-call
                  action is an alternative path rather than a continuation.
                  `my-3` stacks on top of the container's `gap-4` so the divider
                  breathes a little more than the stack's default spacing. */}
              <div
                className="flex items-center gap-3 my-3 text-xs uppercase text-muted-foreground tracking-wider"
                data-testid="gs-automatic-or-divider"
              >
                <div className="flex-1 h-px bg-border" />
                <span>{t('automatic_flow.or')}</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <BookCallOption />
            </div>
          </div>
        ))}

      {state === 'error' && (
        <ErrorView
          category={errorCategory ?? 'unknown'}
          urlHost={submittedUrl ? extractUrlHost(submittedUrl) : undefined}
          onRetry={reset}
          onTryDifferentWebsite={() => {
            // access_blocked's retry means "different URL", so clear the futile draft first.
            effectiveSetUrlDraft('');
            reset();
          }}
          onSwitchToManual={onSwitchToManual}
        />
      )}

      {state === 'processing' && (
        <ProgressView steps={steps} submittedUrl={submittedUrl} warningReason={warningReason} />
      )}

      {state === 'completed' && postStep === 'summary' && (
        <div className="flex flex-col gap-4">
          {steps.length === 0 ? (
            // Resume path: the scrape completed in a prior session (we
            // recovered `state === 'completed'` from the DB-backed progress
            // row) but the WS event stream that populates the agent preview
            // is gone. Render a minimal "ready" card so the user keeps moving
            // forward instead of seeing a misleading "analyzing…" header. A
            // persisted low-quality verdict restores the live summary's
            // warning treatment instead of a celebratory "ready".
            <div className="bg-card border border-border rounded-2xl p-6 flex flex-col gap-6 min-h-[500px]">
              <div>
                <p className="text-xl font-semibold text-foreground">
                  {warningReason
                    ? t('automatic_flow.low_quality.summary_title')
                    : t('automatic_flow.phase.done')}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  {warningReason
                    ? t('automatic_flow.low_quality.summary_hint')
                    : t('automatic_flow.ready_hint')}
                </p>
              </div>
              {warningReason && (
                <Alert variant="warning" data-testid="gs-automatic-low-quality">
                  <AlertTitle>{t('automatic_flow.low_quality.title')}</AlertTitle>
                  <AlertDescription>
                    {resumeSourceDomain
                      ? t(LOW_QUALITY_COPY_KEY[warningReason], { domain: resumeSourceDomain })
                      : t('automatic_flow.low_quality.summary_hint')}
                  </AlertDescription>
                </Alert>
              )}
              <div className="mt-auto flex items-center justify-end gap-3">
                <Button
                  size="sm"
                  variant={warningReason ? 'link' : undefined}
                  onClick={() => advancePostStep('external_apps')}
                  disabled={progressMutation.isPending}
                  className={warningReason ? undefined : 'min-w-16'}
                  data-testid="gs-automatic-summary-next"
                >
                  {progressMutation.isPending && !warningReason ? (
                    <Spinner className="size-4" />
                  ) : (
                    t(warningReason ? 'automatic_flow.low_quality.continue_anyway' : 'continue')
                  )}
                </Button>
                {warningReason && (
                  <Button
                    size="sm"
                    onClick={handleStartOver}
                    className="min-w-16"
                    data-testid="gs-automatic-low-quality-start-over"
                  >
                    {t('automatic_flow.low_quality.start_over')}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <ProgressView
              steps={steps}
              submittedUrl={submittedUrl}
              warningReason={warningReason}
              footer={
                <>
                  {/* Low-quality source: lead with "Start over" (back to the
                      URL screen — retry, different URL, or switch to manual)
                      rather than nudging the user forward with junk context. */}
                  <Button
                    size="sm"
                    variant={warningReason ? 'link' : undefined}
                    onClick={() => advancePostStep('external_apps')}
                    disabled={progressMutation.isPending}
                    className={warningReason ? undefined : 'min-w-16'}
                    data-testid="gs-automatic-summary-next"
                  >
                    {progressMutation.isPending && !warningReason ? (
                      <Spinner className="size-4" />
                    ) : (
                      t(warningReason ? 'automatic_flow.low_quality.continue_anyway' : 'continue')
                    )}
                  </Button>
                  {warningReason && (
                    <Button
                      size="sm"
                      onClick={handleStartOver}
                      className="min-w-16"
                      data-testid="gs-automatic-low-quality-start-over"
                    >
                      {t('automatic_flow.low_quality.start_over')}
                    </Button>
                  )}
                </>
              }
            />
          )}
          <PostStepProgressDots currentStep="summary" />
        </div>
      )}

      {state === 'completed' && postStep === 'external_apps' && (
        <div className="flex flex-col gap-4">
          <PostStepCard
            title={t('steps.gs_step_3.title')}
            description={t('steps.gs_step_3.description')}
            onBack={() => setPostStep('summary')}
            onNext={() => advancePostStep('deployment')}
            nextLabel={t('continue')}
            isAdvancing={progressMutation.isPending}
          >
            <ExternalAppsStep />
          </PostStepCard>
          <PostStepProgressDots currentStep="external_apps" />
        </div>
      )}

      {state === 'completed' && postStep === 'deployment' && (
        <div className="flex flex-col gap-4">
          <PostStepCard
            title={t('steps.gs_step_4.title')}
            description={t('steps.gs_step_4.description')}
            onBack={() => setPostStep('external_apps')}
            onNext={() => advancePostStep('pricing')}
            nextLabel={t('continue')}
            isAdvancing={progressMutation.isPending}
          >
            <DeploymentStep />
          </PostStepCard>
          <PostStepProgressDots currentStep="deployment" />
        </div>
      )}

      {state === 'completed' && postStep === 'pricing' && (
        <div className="flex flex-col gap-4">
          <PricingStepContainer
            onBack={() => setPostStep('deployment')}
            onSkipForNow={handlePricingSkipForNow}
            isSkipPending={progressMutation.isPending}
          />
          <PostStepProgressDots currentStep="pricing" />
        </div>
      )}
    </div>
  );
};
